import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Stamp, Calendar, User, XCircle, Clock, AlertTriangle, Hash, Users, Shield } from "lucide-react";
import { format, addDays } from "date-fns";
import { de } from "date-fns/locale";

interface CommitmentBlockProps {
  data?: {
    recommended_option_id?: string;
    recommended_option_label?: string;
    validity_days?: number;
    owner?: string;
    review_date?: string;
    committed_at?: string;
    forced_commit?: boolean;
    forced_commit_active_users?: string[];
    forced_commit_timestamp?: string;
    excluded_options?: Array<{
      label: string;
      reason?: string;
      consequence?: string;
    }>;
  };
  recommendedOption?: string;
  options?: Array<{
    option_id: string;
    label: string;
  }>;
  commitHash?: string;
  committedAt?: string;
  commitType?: 'standard' | 'forced';
  participantsSnapshot?: Array<{ user_id: string; role: string }>;
  alignmentScoreAtCommit?: number;
  decisionThesisAtCommit?: string;
  sessionSnapshot?: {
    schema_version?: string;
    captured_at?: string;
    data_quality?: { completeness_score?: number; missing_fields?: string[] };
    [key: string]: any;
  };
}

export function CommitmentBlock({ 
  data, 
  recommendedOption, 
  options,
  commitHash,
  committedAt,
  commitType,
  participantsSnapshot,
  alignmentScoreAtCommit,
  decisionThesisAtCommit,
  sessionSnapshot,
}: CommitmentBlockProps) {
  const hasCommitment = data?.validity_days || data?.committed_at || committedAt;
  
  const optionLabel = data?.recommended_option_label || recommendedOption || "Gewählte Option";
  const validityDays = data?.validity_days || 90;
  const owner = data?.owner;
  const reviewDate = data?.review_date 
    ? new Date(data.review_date) 
    : addDays(new Date(), validityDays);
  
  // Only use excluded_options from backend if they have real reasons
  const excludedOptions = data?.excluded_options?.filter(
    opt => opt.reason || opt.consequence
  ) || [];

  const displayHash = commitHash?.substring(0, 8);
  const displayTimestamp = committedAt || data?.committed_at;
  const effectiveCommitType = commitType || (data?.forced_commit ? 'forced' : 'standard');

  return (
    <Card id="commitment" className="scroll-mt-24 border-2 border-green-500/30 bg-gradient-to-br from-green-500/5 to-transparent">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Stamp className="h-5 w-5 text-green-600" />
            Verbindlicher Commit
          </CardTitle>
          <div className="flex items-center gap-2">
            {effectiveCommitType === 'forced' ? (
              <Badge variant="outline" className="text-amber-600 border-amber-500/30 gap-1">
                <AlertTriangle className="h-3 w-3" />
                Erzwungen
              </Badge>
            ) : hasCommitment ? (
              <Badge className="bg-green-500/20 text-green-700 border-green-500/30 gap-1">
                <Shield className="h-3 w-3" />
                Standard
              </Badge>
            ) : (
              <Badge variant="outline" className="text-amber-600 border-amber-500/30">
                Ausstehend
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Commitment Statement */}
        <div className="user-input bg-green-500/10 rounded-lg p-4 border border-green-500/20">
          <p className="text-base font-semibold text-green-800 dark:text-green-300">
            Für die Dauer von {validityDays} Tagen werden keine parallelen Strategien verfolgt.
          </p>
          <p className="text-sm text-green-700 dark:text-green-400 mt-2">
            Gewählte Option: <strong>{optionLabel}</strong>
          </p>
        </div>

        {/* Audit Metadata Row */}
        {(displayHash || displayTimestamp || participantsSnapshot) && (
          <div className="flex flex-wrap gap-3">
            {/* Commit Hash */}
            {displayHash && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-muted/50 rounded-lg border border-border/50">
                <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Hash</span>
                <code className="text-xs font-mono font-semibold text-foreground">{displayHash}</code>
              </div>
            )}

            {/* Commit Timestamp */}
            {displayTimestamp && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-muted/50 rounded-lg border border-border/50">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs font-medium">
                  {new Date(displayTimestamp).toLocaleDateString('de-DE', { 
                    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                  })}
                </span>
              </div>
            )}

            {/* Participants Count */}
            {participantsSnapshot && participantsSnapshot.length > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-muted/50 rounded-lg border border-border/50">
                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs font-medium">
                  {participantsSnapshot.length} Teilnehmer
                </span>
              </div>
            )}

            {/* Data Quality at Commit */}
            {sessionSnapshot?.data_quality?.completeness_score != null && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-muted/50 rounded-lg border border-border/50">
                <Shield className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Datenqualität</span>
                <span className="text-sm font-bold font-mono tabular-nums">{sessionSnapshot.data_quality.completeness_score}%</span>
              </div>
            )}

            {/* Alignment Score at Commit */}
            {alignmentScoreAtCommit != null && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-primary/5 rounded-lg border border-primary/20">
                <span className="text-xs text-muted-foreground">Alignment</span>
                <span className="text-sm font-bold font-mono text-primary tabular-nums">{alignmentScoreAtCommit}</span>
              </div>
            )}
          </div>
        )}

        {/* Decision Thesis at Commit */}
        {decisionThesisAtCommit && (
          <div className="p-3 bg-muted/30 rounded-lg border border-border/50">
            <p className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide mb-1">
              Entscheidungsthese zum Zeitpunkt des Commits
            </p>
            <p className="text-sm italic text-foreground/80 font-serif leading-relaxed">
              „{decisionThesisAtCommit}"
            </p>
          </div>
        )}

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Validity */}
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <Clock className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-muted-foreground">Gültigkeitsdauer</p>
              <p className="text-sm font-semibold">{validityDays} Tage</p>
              {data?.review_date && (
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Validierung am {format(new Date(data.review_date), "d. MMM", { locale: de })} (Go/Pivot Gate)
                </p>
              )}
            </div>
          </div>

          {/* Review Date */}
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <Calendar className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-muted-foreground">Review-Datum</p>
              <p className="text-sm font-semibold">
                {format(reviewDate, "d. MMMM yyyy", { locale: de })}
              </p>
            </div>
          </div>

          {/* Owner */}
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <User className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-muted-foreground">Decision Owner</p>
              <p className="text-sm font-semibold">{owner || "Nicht zugewiesen"}</p>
            </div>
          </div>
        </div>

        {/* Forced Commit Audit Warning */}
        {(data?.forced_commit || effectiveCommitType === 'forced') && (
          <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                Commit erfolgte trotz aktiver Bearbeitung
              </p>
              <p className="text-xs text-amber-700/80 dark:text-amber-400/70 mt-0.5">
                Zum Zeitpunkt des Commits {data?.forced_commit_active_users?.length === 1 ? 'war 1 Person' : `waren ${data?.forced_commit_active_users?.length || 'weitere Personen'}`} aktiv in dieser Session.
                {data?.forced_commit_timestamp && (
                  <> Erzwungen am {new Date(data.forced_commit_timestamp).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}.</>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Excluded Options - only show if real reasons exist */}
        {excludedOptions.length > 0 && (
          <div className="pt-4 border-t">
            <div className="flex items-center gap-2 mb-3">
              <XCircle className="h-4 w-4 text-muted-foreground" />
              <h4 className="text-sm font-semibold text-muted-foreground">Bewusste Ausschlüsse</h4>
            </div>
            <div className="space-y-2">
              {excludedOptions.map((opt, index) => {
                const displayReason = opt.consequence || opt.reason;
                return (
                  <div key={index} className="flex items-start gap-2 text-sm">
                    <span className="text-red-500 shrink-0">✕</span>
                    <div>
                      <span className="font-medium">{opt.label}</span>
                      {displayReason && (
                        <span className="text-muted-foreground"> – {displayReason}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer Note */}
        <p className="text-xs text-muted-foreground text-center pt-3 border-t">
          Änderungen an dieser Entscheidung erfordern eine neue Bewertung in Phase 3.
        </p>
      </CardContent>
    </Card>
  );
}
